import { BaseWidget, Container } from "../widgets";

/**
 * Renders a single node in the minimap. Containers are drawn as
 * nested boxes honoring their flex direction and background color.
 * Leaf widgets are neutral blocks with an accent strip using the
 * widget's own text color setting.
 *
 * @param {object} props
 * @param {BaseWidget} props.widget - The widget to render
 * @param {BaseWidget[]} props.widgets - Full flat widget list
 * @param {(widget: BaseWidget) => void} props.onClickWidget
 * @param {(widget: BaseWidget|null) => void} props.onMouseOver
 */
const MiniMapNode = ({ widget, widgets, onClickWidget, onMouseOver }) => {

    const children = widgets
        .filter(w => w.parent === widget.id && !w.deleted)
        .sort((a, b) => a.order - b.order);

    if (widget instanceof Container) {

        const flex       = widget.getSetting('flex');
        const background = widget.getSetting('background');

        const flexDirection     = flex?.flexDirection || 'column';
        const backgroundColor   = background?.backgroundColor || 'transparent';

        return (
            <div
                className="minimap-container"
                style={{ flexDirection, backgroundColor }}
                title={widget.getName()}
                onMouseOver={e => { e.stopPropagation(); onMouseOver(widget); }}
                onMouseLeave={() => onMouseOver(null)}
                onClick={e => { e.stopPropagation(); onClickWidget(widget); }}
            >
                {children.length > 0
                    ? children.map(child => (
                        <MiniMapNode
                            key={child.id}
                            widget={child}
                            widgets={widgets}
                            onClickWidget={onClickWidget}
                            onMouseOver={onMouseOver}
                        />
                    ))
                    : <div className="minimap-empty-slot" />
                }
            </div>
        );
    }

    /* Leaf widget — use the widget's own color setting as an accent */
    const accentColor = widget.getSetting('color') || '#888';

    return (
        <div
            className="minimap-leaf"
            style={{ color: accentColor }}
            title={widget.getName()}
            onMouseOver={e => { e.stopPropagation(); onMouseOver(widget); }}
            onMouseLeave={() => onMouseOver(null)}
            onClick={e => { e.stopPropagation(); onClickWidget(widget); }}
        >
            <span className="minimap-leaf-icon">{widget.getIcon()}</span>
            <span className="minimap-leaf-label">{widget.getName()}</span>
        </div>
    );
};

/**
 * Miniature map view of the document structure.
 * Containers render as nested boxes (respecting flex direction and
 * background color). Leaf widgets show a left accent in their own
 * text color.
 *
 * @param {object} props
 * @param {BaseWidget[]} props.widgets - Full flat widget list
 * @param {(widget: BaseWidget) => void} props.onClickWidget
 *     Callback when a widget block is clicked
 * @param {(widget: BaseWidget|null) => void} props.onMouseOver
 *     Callback when a widget block is hovered (null on leave)
 */
export const MiniMap = ({ widgets, onClickWidget, onMouseOver }) => {

    const roots = widgets
        .filter(w => w.parent === null && !w.deleted)
        .sort((a, b) => a.order - b.order);

    return (
        <div className="report-builder-minimap">
            <div className="minimap-canvas">
                {roots.map(w => (
                    <MiniMapNode
                        key={w.id}
                        widget={w}
                        widgets={widgets}
                        onClickWidget={onClickWidget}
                        onMouseOver={onMouseOver}
                    />
                ))}
            </div>
        </div>
    );
};
